// Единственный QueryClient приложения (app.md §4–5).
//
// Server state живёт здесь и только здесь: класть ответы API в Zustand запрещено.

import { MutationCache, QueryClient } from '@tanstack/react-query';

import { reportSupportDenied, SupportReadOnlyError } from '@/shared/auth';

import { HttpError, NetworkError } from './httpClient';

/**
 * Ретраим только недоставленные запросы. Ответ сервера 4xx — это решение сервера,
 * повторять его бессмысленно; 5xx повторяем ограниченно.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof NetworkError) {
    return failureCount < 3;
  }
  if (error instanceof HttpError) {
    return error.status >= 500 && failureCount < 2;
  }
  return false;
}

/** Запись отклонена потому, что идёт саппорт-сессия платформы (T-039). */
export function isSupportReadOnly(error: unknown): boolean {
  if (error instanceof SupportReadOnlyError) return true;
  if (!(error instanceof HttpError) || error.status !== 403) return false;
  const payload = error.payload as { error?: { code?: string } } | undefined;
  return payload?.error?.code === 'support_read_only';
}

export const queryClient = new QueryClient({
  // Любая мутация, отклонённая саппорт-сессией, — сигнал баннеру сессии. Здесь, а не
  // в экранах: экраны про саппорт не знают, и часть действий отказывала молча.
  mutationCache: new MutationCache({
    onError: (error) => {
      if (isSupportReadOnly(error)) reportSupportDenied();
    },
  }),
  defaultOptions: {
    queries: {
      // offlineFirst: запрос уходит даже когда браузер считает, что сети нет.
      // На кухонном планшете navigator.onLine врёт регулярно, а данные могут
      // прийти из кэша Query или из IndexedDB (offline-first.md §3).
      networkMode: 'offlineFirst',
      staleTime: 60_000,
      gcTime: 30 * 60_000,
      refetchOnWindowFocus: false,
      retry: shouldRetry,
    },
    mutations: {
      // Мутации ядра (печать, аннулирование, списание) идут через очередь синхронизации
      // (offline-first.md §5), а не через ретраи транспортного слоя. Очередь — T-007.
      networkMode: 'offlineFirst',
      retry: false,
    },
  },
});
